import React, { useState } from "react";
import { GetServerSidePropsContext } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { ParsedUrlQuery } from "querystring";
import { withLayout } from "src/components/Layout/withLayout";
import styled from "styled-components";
import CarouselBanner from "src/components/icth-product/main/CarouselBanner";
import ProductCategoryCardList from "src/components/icth-product/main/ProductCategoryCardList";
import ProductCardList from "src/components/icth-product/main/ProductCardList";
import RatingCard from "src/components/icth-product/main/RatingSection/RatingCard";
import CtaSection from "src/components/icth-product/main/CtaSection";
import { mockProducts } from "src/components/icth-product/main/mockProductData";
import InsurerSection from "src/components/icth-product/main/InsurerSection";
import AwardSection from "src/components/icth-product/main/AwardSection";

export interface CustomParams extends ParsedUrlQuery {
  channelPath: string;
}

export async function getServerSideProps(
  context: GetServerSidePropsContext<CustomParams>
) {
  const channelPath = context.params?.channelPath;
  const { locale } = context;

  // mocked channel config to bypass, just testing component
  const channelConfig = {
    code: "RET",
    theme: {},
    content: {
      supportedLangs: [{ code: "th" }, { code: "en" }],
      webHeader: {
        title: { th: "Home Page", en: "Home Page" },
        description: { th: "Home Page", en: "Home Page" },
      },
    },
  };

  return {
    props: {
      channelPath,
      channelConfig,
      ...(await serverSideTranslations(locale || "th", [
        "common",
        "layout",
        "cookie-consent",
        "icth-retail-main",
      ])),
    },
  };
}

const PageWrapper = styled.div`
  padding: 32px 0 0 0;
  min-height: 80vh;
  background-color: #fafafa;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  overflow-x: hidden;

  /* "JUST Sans" is first in the stack, so numbers (0-9) always render in JUST Sans for both th & en paths */
  font-family: "JUST Sans", "Noto Sans Thai", -apple-system, BlinkMacSystemFont,
    "Segoe UI", Roboto, sans-serif;
  font-weight: 500;

  &.lang-en {
    font-family: "JUST Sans", -apple-system, BlinkMacSystemFont, sans-serif;
    font-weight: 400;

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      font-family: "JUST Sans", -apple-system, BlinkMacSystemFont, sans-serif;
      font-weight: 700;
      -webkit-text-stroke: 0.2px currentColor;
    }

    p,
    span,
    div,
    a,
    button {
      font-weight: 400;
    }
  }

  @media (max-width: 768px) {
    padding: 8px 0 0 0;
  }
`;

const ProductSectionWrapper = styled.div`
  width: 100%;
  padding: 0 2rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 3;

  @media (max-width: 768px) {
    padding: 0 12px;
  }
`;

const CarouselWrapper = styled.div`
  width: 100%;
  margin-bottom: 40px;
  display: flex;
  justify-content: center;

  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const RatingAndCtaContainer = styled.div<{ $hasSelectedCard?: boolean }>`
  --curve-height: clamp(30px, 4.1666vw, 80px);
  position: relative;
  width: 100%;
  max-width: 1920px;
  margin: ${(props) =>
    props.$hasSelectedCard ? "0 auto" : "100px auto 0 auto"};
  min-height: auto;
  background-color: #e8effd;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  z-index: 4;

  @media (max-width: 1200px) {
    min-height: auto;
    padding-top: 40px;
    margin: ${(props) =>
      props.$hasSelectedCard ? "0 auto" : "60px auto 0 auto"};
  }

  &::before {
    content: "";
    position: absolute;
    top: calc(-1 * var(--curve-height));
    left: 0;
    width: 100%;
    height: calc(var(--curve-height) + 2px);
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 80" preserveAspectRatio="none"><path d="M0,0 Q720,80 1440,0 L1440,80 L0,80 Z" fill="%23e8effd"/></svg>');
    background-repeat: no-repeat;
    background-size: 100% 100%;
    pointer-events: none;
    z-index: 1;
  }

  & > * {
    position: relative;
    z-index: 2;
  }

  @media (max-width: 768px) {
    margin: ${(props) =>
      props.$hasSelectedCard ? "0 auto" : "40px auto 0 auto"};
    padding: 0;
    gap: 0;
  }
`;

const HomePage = () => {
  const { t, i18n } = useTranslation("icth-retail-main");
  const [selectedCard, setSelectedCard] = useState<string | null>(null);

  const handleCardClick = (categoryId: string) => {
    setSelectedCard((prev) => (prev === categoryId ? null : categoryId));
  };

  const selectedProducts = mockProducts.filter(
    (product) => product.category === selectedCard
  );

  const categoriesList = t("productCategory.list", {
    returnObjects: true,
  }) as Array<{
    id: string;
    title: string;
  }>;

  const currentCategory = Array.isArray(categoriesList)
    ? categoriesList.find((cat) => cat.id === selectedCard)
    : null;

  const currentCategoryTitle = currentCategory
    ? currentCategory.title.replace(/\n/g, " ")
    : "";

  return (
    <PageWrapper className={i18n.language === "en" ? "lang-en" : ""}>
      <ProductSectionWrapper>
        <CarouselWrapper>
          <CarouselBanner
            images={[
              "/static/carousel-banner-1.png",
              "/static/carousel-banner-2.png",
            ]}
            mobileImages={[
              "/static/carousel-banner-1.png",
              "/static/carousel-banner-2-mobile.png",
            ]}
          />
        </CarouselWrapper>
        <ProductCategoryCardList
          selectedCard={selectedCard}
          onCardClick={handleCardClick}
        />
      </ProductSectionWrapper>
      {selectedCard && (
        <div
          style={{
            marginTop: "23px",
            marginBottom: "0px",
            width: "100%",
            position: "relative",
            zIndex: 2,
          }}
        >
          <ProductCardList
            products={selectedProducts}
            title={currentCategoryTitle || undefined}
          />
        </div>
      )}
      <RatingAndCtaContainer $hasSelectedCard={!!selectedCard}>
        <RatingCard />
        <CtaSection />
        <InsurerSection />
      </RatingAndCtaContainer>
      <AwardSection />
    </PageWrapper>
  );
};

export const ContentGroup = () => (
  <>
    <RatingAndCtaContainer $hasSelectedCard={false}>
      <RatingCard />
      <CtaSection />
      <InsurerSection />
    </RatingAndCtaContainer>
    <AwardSection />
  </>
);

export default withLayout(HomePage);
